import { IconNames } from '@blueprintjs/icons';
import { skipToken } from '@reduxjs/toolkit/query';
import { type BenefitType, BenefitTypes, type ReferentPriceType } from '@strata/constants';
import { DollarAmount } from '@strata/react-dollar-amount';
import { type ColumnDef, Grid, SecondaryData } from '@strata/react-grid';
import { InlineMessage } from '@strata/react-inline-message';
import { formatRateString } from '@strata/react-servicing-rate';
import { Icon } from '@strata/react-status-icons';
import classNames from 'classnames';
import type dayjs from 'dayjs';
import Decimal from 'decimal.js';
import { useEffect } from 'react';

import { GridHeaderWithInfo } from 'components/shared/GridHeaderWithInfo';
import { useGetEligiblePricesForOidQuery } from 'store/api/referenceApi';

const NOT_SELECTED = 'NOT_SELECTED';

interface UnitPriceGridRow {
    code: string;
    price?: string;
    currentBidPrice?: undefined;
}

const NotSelectedRow = ({ isPharmacyBenefit }: { isPharmacyBenefit: boolean }) => {
    useEffect(() => {
        document
            .getElementsByClassName('sds_unit-price-grid--not-selected')?.[0]
            ?.children?.[0]?.setAttribute('colspan', '3');
    }, []);

    return <div>{isPharmacyBenefit ? 'NDC' : 'Procedure'} and Date of Service not selected.</div>;
};

const columns = ({
    isPharmacyBenefit,
    priceIsFrozen = false,
    priceFrozenMsg,
    currentBid,
}: {
    isPharmacyBenefit: boolean;
    priceIsFrozen?: boolean;
    priceFrozenMsg: string;
    currentBid?: number;
}): ColumnDef<UnitPriceGridRow>[] => [
    {
        accessorKey: 'code',
        header: isPharmacyBenefit ? 'NDC' : 'HCPCS',
        size: 100,
        cell: ({ getValue }) =>
            getValue() !== NOT_SELECTED ? (
                <SecondaryData data={getValue()} />
            ) : (
                <NotSelectedRow isPharmacyBenefit={isPharmacyBenefit} />
            ),
    },
    {
        accessorKey: 'price',
        header: 'Unit Price',
        size: 100,
        cell: ({ getValue }) => (
            <span className="flex__gap--small" style={{ alignItems: 'center' }}>
                <DollarAmount amount={getValue()} showZeroValue />
            </span>
        ),
    },
    {
        accessorKey: 'currentBidPrice',
        header: () => (
            <GridHeaderWithInfo
                header="Servicing Unit Price"
                tooltipText="Servicing unit price is the unit price multiplied by the servicing rate."
            />
        ),
        size: 100,
        cell: ({ row }) => (
            <span className="flex__gap--small" style={{ alignItems: 'center' }}>
                <DollarAmount
                    amount={
                        currentBid == null ||
                        Number.isNaN(currentBid) ||
                        formatRateString({ bidDecimal: currentBid }) === 'Contract Rate' ||
                        Number.isNaN(parseFloat(row.original.price!))
                            ? undefined
                            : Decimal.mul(
                                  Decimal.add(1, Decimal.div(currentBid, 100)),
                                  row.original.price!,
                              ).toNumber()
                    }
                    showZeroValue
                />
                {priceIsFrozen && (
                    <Icon size={12} icon={IconNames.LOCK} tooltipContent={priceFrozenMsg} />
                )}
            </span>
        ),
    },
];

interface UnitPriceGridProps {
    priceFrozenMsg: string;
    code?: string;
    codeRequired?: boolean;
    priceDate?: dayjs.ConfigType;
    currentBid?: number;
    referralDetails?: {
        productServiceOid: string;
        referentPriceType?: ReferentPriceType;
        isBrand: boolean;
        priceSource?: string;
        priceIsFrozen?: boolean;
        benefit: BenefitType;
    };
}

// use referral start if is_frozen
export const UnitPriceGrid = ({
    priceFrozenMsg,
    code = undefined,
    codeRequired = false,
    priceDate = undefined,
    currentBid = undefined,
    referralDetails = undefined,
}: UnitPriceGridProps) => {
    const priceIsFrozen = referralDetails?.priceIsFrozen;
    const isPharmacyBenefit = referralDetails?.benefit === BenefitTypes.PHARMACY;

    const { data: unitPrices, isFetching } = useGetEligiblePricesForOidQuery(
        referralDetails?.referentPriceType && referralDetails.priceSource && priceDate
            ? {
                  oid: referralDetails.productServiceOid,
                  referentPriceType: referralDetails.referentPriceType,
                  isBrand: referralDetails.isBrand,
                  priceSource: referralDetails.priceSource,
                  priceDate,
                  benefitType: referralDetails.benefit,
              }
            : skipToken,
    );

    const matchingPrices = unitPrices?.filter(
        (price) => (!codeRequired && code == null) || price.code === code,
    );

    return !isFetching ? (
        <div className="flex__gap--medium" style={{ flexDirection: 'column' }}>
            {priceIsFrozen && (
                <InlineMessage intent={InlineMessage.Intent.INFO} label={priceFrozenMsg} />
            )}
            <Grid<UnitPriceGridRow>
                data={
                    !codeRequired || (priceDate && code) ? matchingPrices : [{ code: NOT_SELECTED }]
                }
                columns={columns({ isPharmacyBenefit, priceIsFrozen, priceFrozenMsg, currentBid })}
                rowCssClass={(rowData) =>
                    classNames({
                        'sds_unit-price-grid--not-selected': rowData.code === NOT_SELECTED,
                    })
                }
            />
        </div>
    ) : null;
};
